import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { createRole, deleteRole, rolesUser, updateRole } from "../services/api";
import { setAlert } from "../store/slices/alertSlice";

const emptyForm = {
  roleName: "",
  permissions: ""
};

const RoleManagement = () => {
  const dispatch = useDispatch();
  const [roles, setRoles] = useState([]);
  const [formData, setFormData] = useState(emptyForm);
  const [editingId, setEditingId] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");

  const filteredRoles = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return roles;

    return roles.filter((role) =>
      [role.roleName, ...(role.permissions || [])]
        .join(" ")
        .toLowerCase()
        .includes(term)
    );
  }, [roles, search]);

  const loadRoles = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await rolesUser();
      setRoles(Array.isArray(data) ? data : []);
    } catch (error) {
      dispatch(setAlert({ type: "danger", message: "Error loading roles" }));
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    loadRoles();
  }, [loadRoles]);

  const resetForm = () => {
    setFormData(emptyForm);
    setEditingId("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!formData.roleName.trim()) {
      dispatch(setAlert({ type: "danger", message: "Role name is required" }));
      return;
    }

    const payload = {
      roleName: formData.roleName.trim(),
      permissions: formData.permissions
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean)
    };

    setSaving(true);
    try {
      if (editingId) {
        await updateRole(editingId, payload);
        dispatch(setAlert({ type: "success", message: "Role updated successfully" }));
      } else {
        await createRole(payload);
        dispatch(setAlert({ type: "success", message: "Role created successfully" }));
      }

      resetForm();
      loadRoles();
    } catch (error) {
      dispatch(setAlert({
        type: "danger",
        message: error.response?.data?.error || "Unable to save role"
      }));
    } finally {
      setSaving(false);
    }
  };

  const editRole = (role) => {
    setEditingId(role._id);
    setFormData({
      roleName: role.roleName || "",
      permissions: (role.permissions || []).join(", ")
    });
  };

  const removeRole = async (role) => {
    if (!window.confirm(`Delete role "${role.roleName}"?`)) return;

    try {
      await deleteRole(role._id);
      dispatch(setAlert({ type: "success", message: "Role deleted successfully" }));
      loadRoles();
    } catch (error) {
      dispatch(setAlert({ type: "danger", message: "Unable to delete role" }));
    }
  };

  return (
    <div className="admin-page">
      <div className="admin-header">
        <div>
          <h3>Roles</h3>
          <p>Create and maintain user roles for the incident workflow.</p>
        </div>
      </div>

      <div className="admin-layout">
        <form className="admin-form-panel" onSubmit={handleSubmit}>
          <div className="admin-panel-title">{editingId ? "Edit Role" : "Create Role"}</div>

          <label>
            <span>Role Name</span>
            <input
              className="form-control"
              value={formData.roleName}
              onChange={(event) => setFormData((prev) => ({ ...prev, roleName: event.target.value }))}
              placeholder="Quality Manager"
            />
          </label>

          <label>
            <span>Permissions</span>
            <textarea
              className="form-control"
              rows="4"
              value={formData.permissions}
              onChange={(event) => setFormData((prev) => ({ ...prev, permissions: event.target.value }))}
              placeholder="Comma separated permissions"
            />
          </label>

          <div className="admin-form-actions">
            {editingId && (
              <button type="button" className="admin-secondary-btn" onClick={resetForm}>
                Cancel
              </button>
            )}
            <button type="submit" className="admin-primary-btn" disabled={saving}>
              <i className={`bi ${editingId ? "bi-check2" : "bi-plus-lg"}`} aria-hidden="true"></i>
              <span>{saving ? "Saving" : editingId ? "Update Role" : "Create Role"}</span>
            </button>
          </div>
        </form>

        <div className="dashboard-list-panel admin-grid-panel">
          <div className="dashboard-list-header">
            <div>
              <div className="admin-grid-title">Role List</div>
              <div className="list-count">{filteredRoles.length} records</div>
            </div>
            <div className="dashboard-table-tools">
              <label>
                Search
                <input
                  className="admin-search-input"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Role or permission"
                />
              </label>
              <button
                type="button"
                className="grid-icon-btn"
                onClick={loadRoles}
                aria-label="Refresh roles"
                title="Refresh roles"
              >
                <i className="bi bi-arrow-repeat" aria-hidden="true"></i>
              </button>
            </div>
          </div>

          <div className="modern-table-wrap">
            <table className="table modern-dashboard-table">
              <thead>
                <tr>
                  <th>Sl No</th>
                  <th>Role Name</th>
                  <th>Permissions</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredRoles.length ? filteredRoles.map((role, index) => (
                  <tr key={role._id}>
                    <td>{index + 1}</td>
                    <td className="admin-strong-cell">{role.roleName}</td>
                    <td>
                      <div className="admin-chip-list">
                        {(role.permissions || []).length ? role.permissions.map((permission) => (
                          <span className="admin-chip" key={permission}>{permission}</span>
                        )) : <span className="text-muted small">No permissions</span>}
                      </div>
                    </td>
                    <td>
                      <div className="admin-row-actions">
                        <button type="button" className="admin-icon-action" onClick={() => editRole(role)} title="Edit role">
                          <i className="bi bi-pencil" aria-hidden="true"></i>
                        </button>
                        <button type="button" className="admin-icon-action danger" onClick={() => removeRole(role)} title="Delete role">
                          <i className="bi bi-trash" aria-hidden="true"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan="4" className="text-center text-muted">
                      {loading ? "Loading..." : "No roles found"}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RoleManagement;
